import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Grid, Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { get } from 'api';
import { url } from 'config/config';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const Salary = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userId: '',
      monthlyCTC: '',
      effectiveFrom: '',
      isAttendanceRequired: true,
    },
  });

  const {
    control: appraisalControl,
    handleSubmit: handleAppraisalSubmit,
    reset: resetAppraisal,
    formState: { errors: appraisalErrors },
  } = useForm({
    defaultValues: {
      newMonthlyCTC: '',
      effectiveFrom: '',
      isAttendanceRequired: true,
    },
  });
  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 280,
        // flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { firstName, lastName, profileImage, empId } = params.row.user;

          return (
            <Stack
              sx={{
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={firstName.toUpperCase()}
                src={profileImage ? `${url}${profileImage}` : ''}
                sx={{ width: 24, height: 24 }}
              />
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {`${firstName} ${lastName}`}
                </Typography>
                <Typography variant="caption">{`${empId}`}</Typography>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Designation',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.user.role}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'monthlyCTC',
        headerName: 'Monthly CTC',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.monthlyCTC}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'efectiveFrom',
        headerName: 'Effective From',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.isActive
                  ? moment(params.row.effectiveFrom).format('DD/MM/YYYY')
                  : `${moment(params.row.effectiveFrom).format('DD/MM/YYYY')} - ${moment(params.row.effectiveTo).format('DD/MM/YYYY')}`}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'isAttendanceRequired',
        headerName: 'Attendance Required',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.isAttendanceRequired ? 'Yes' : 'No'}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'appraisal',
        headerName: 'Appraisal',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <IconButton
                color={params.row.isActive ? 'success' : 'error'}
                disabled={!params.row.isActive}
                onClick={() => {
                  setOpenAppraisal(true);
                  setSelected(params.row);
                }}
              >
                <TrendingUpIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [],
  );
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: search,
  });
  const [candidate, setCandidate] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAppraisal, setOpenAppraisal] = useState(false);
  const [selected, setSelected] = useState(false);
  const [User, setUser] = useState([]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        monthlyCTC: Number(data.monthlyCTC),
        effectiveFrom: moment(data.effectiveFrom).startOf('month').toDate(),
        isAttendanceRequired: data.isAttendanceRequired == 'true' ? true : false,
        userId: data.userId,
      };

      const res = await post('/admin/create_salary', payload);

      if (res.status === 200) {
        enqueueSnackbar('Salary Added', { variant: 'success' });
        setOpen(false);
        reset();
        fetchData();
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    }
  };

  const onSubmitAppraisal = async (data) => {
    try {
      const payload = {
        userId: selected.user._id,
        newMonthlyCTC: Number(data.newMonthlyCTC),
        effectiveFrom: moment(data.effectiveFrom).startOf('month').toDate(),
        salaryId: selected._id,
      };

      const res = await post('/admin/appraisal_salary', payload);

      if (res.status === 200) {
        enqueueSnackbar('Salary Increased Successfully', { variant: 'success' });

        setOpenAppraisal(false);
        resetAppraisal();
        fetchData();
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search: search }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await post('/admin/list_salary', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      if (res.status == 200) {
        setCandidate(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
        setCandidate([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
      setCandidate([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await get('/users/dropdown_user', { isActive: true });
      if (res.status == 200) {
        setUser(res.data);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
        setUser([]);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
      setUser([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  return (
    <>
      <Paper sx={{ flex: 1, p: { xs: 3, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <StyledTextField
            id="search-box"
            type="search"
            fullWidth
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Candidate Name"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              maxWidth: { sm: 200, md: 240 },
              flexGrow: { xs: 1, sm: 0 },
            }}
          />
          <Button
            variant="soft"
            shape="square"
            color="neutral"
            sx={{ gap: 0.5, p: 1 }}
            onClick={() => setOpen(true)}
          >
            <IconifyIcon icon="material-symbols:add-2-rounded" />
            <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Add Salary
            </Box>
          </Button>
        </Box>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={candidate}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel} // { page: 0, pageSize: 10 }
            onPaginationModelChange={(e) =>
              setPaginationModel(() => ({
                page: e.page,
                pageSize: e.pageSize,
                search,
              }))
            }
            pageSizeOptions={[5, 10, 20]}
            slots={{
              basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
            }}
            getRowClassName={(params) => (params.row.isActive ? '' : 'low-stock-row')}
          />
        </Box>
      </Paper>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add Salary</DialogTitle>

        <DialogContent sx={{ mt: 2, minWidth: 400 }}>
          <Grid container spacing={2}>
            {/* Monthly CTC */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="monthlyCTC"
                control={control}
                rules={{ required: 'Monthly CTC is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Monthly CTC"
                    type="number"
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        min: moment().format('YYYY-MM'),
                      },
                    }}
                    error={!!errors.monthlyCTC}
                    helperText={errors.monthlyCTC?.message}
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="userId"
                control={control}
                rules={{ required: 'UserId is required' }}
                render={({ field }) => (
                  <>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select User</InputLabel>
                      <Select {...field} label="Reporting Manager">
                        {User.map((u) => (
                          <MenuItem key={u._id} value={u._id}>
                            {`${u.firstName} ${u.lastName}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              {' '}
              {/* Effective From */}
              <Controller
                name="effectiveFrom"
                control={control}
                rules={{ required: 'Effective Month is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Effective From"
                    type="month"
                    fullWidth
                    error={!!errors.effectiveFrom}
                    helperText={errors.effectiveFrom?.message}
                  />
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              {/* Attendance Required */}
              <Controller
                name="isAttendanceRequired"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    label="Attendance Required"
                    fullWidth
                    SelectProps={{ native: true }}
                    {...field}
                  >
                    <option value={true}>Yes</option>
                    <option value={false}>No</option>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={() => setOpen(false)}>
            Cancel
          </Button>

          <Button onClick={handleSubmit(onSubmit)}>Submit</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAppraisal} onClose={() => setOpenAppraisal(false)}>
        <DialogTitle>
          Increase Salary - {`${selected?.user?.firstName} ${selected?.user?.lastName}`}
        </DialogTitle>

        <DialogContent sx={{ mt: 2, minWidth: 400 }}>
          <Grid container spacing={2}>
            {/* Monthly CTC */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="newMonthlyCTC"
                control={appraisalControl}
                rules={{ required: 'New Monthly CTC is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="New Monthly CTC"
                    type="number"
                    fullWidth
                    error={!!appraisalErrors.newMonthlyCTC}
                    helperText={appraisalErrors.newMonthlyCTC?.message}
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>
            {/* <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="userId"
                control={control}
                rules={{ required: 'UserId is required' }}
                render={({ field }) => (
                  <>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Select User</InputLabel>
                      <Select {...field} label="Reporting Manager">
                        {User.map((u) => (
                          <MenuItem key={u._id} value={u._id}>
                            {`${u.firstName} ${u.lastName}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              />
            </Grid> */}

            <Grid item size={{ xs: 12, sm: 6 }} sx={{ mt: 1 }}>
              {' '}
              {/* Effective From */}
              <Controller
                name="effectiveFrom"
                control={appraisalControl}
                rules={{ required: 'Effective Month is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Effective From"
                    type="month"
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        min: moment().format('YYYY-MM'),
                      },
                    }}
                    error={!!appraisalErrors.effectiveFrom}
                    helperText={appraisalErrors.effectiveFrom?.message}
                  />
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              {/* Attendance Required */}
              <Controller
                name="isAttendanceRequired"
                control={appraisalControl}
                render={({ field }) => (
                  <TextField
                    select
                    label="Attendance Required"
                    fullWidth
                    SelectProps={{ native: true }}
                    {...field}
                  >
                    <option value={true}>Yes</option>
                    <option value={false}>No</option>
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={() => setOpen(false)}>
            Cancel
          </Button>

          <Button onClick={handleAppraisalSubmit(onSubmitAppraisal)}>Submit</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Salary;
